import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { ConfirmModal } from 'components/dialog/ModalTemplate'
import {
  getEtcPresets,
  getGeneralPresets,
  MetricPresets,
} from 'features/dashboard/table/shared/Presets'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { DashboardTypes } from 'features/Codes'
import {
  applyUserPreset,
  fetchUserPresets,
  hidePresetConfigModal,
  saveUserPreset,
  setColumns,
  updateUserPreset,
} from 'features/dashboard/table/config/tableConfigSlice'
import SortableColumnList from 'features/dashboard/table/config/SortableList'
import ScrollablePresetSelector from 'features/dashboard/table/config/ScrollablePresetSelector'
import classNames from 'classnames'
import CheckBox from 'components/form/CheckBox'
import TextInput from 'components/form/TextInput'
import { isPresent } from 'utils'
import ErrorText from 'components/form/ErrorText'
import LoadingWrapper from 'components/loading/LoadingWrapper'
import { success } from 'features/toast/Toast'
import { Modal } from 'components/dialog/Modals'
import useValidator from 'utils/hooks/useValidator'

import { Columns } from '../../../Columns'

const PresetConfigModal = ({ domainType }) => {
  const dispatch = useDispatch()
  const [shouldSavePreset, setShouldSavePreset] = useState(false)
  const [presetName, setPresetName] = useState('')
  const { errors, validate } = useValidator(
    () => ({
      presetName:
        shouldSavePreset &&
        presetName.trim() === '' &&
        '저장할 열 설정 이름을 입력하세요.',
    }),
    [shouldSavePreset, presetName]
  )
  const { visible, editing, columns, loading, isLoaded, userPresets } =
    useSelector(
      ({ dashboard }) => ({
        visible: dashboard.table.config.visible[domainType],
        editing: dashboard.table.config.editing[domainType],
        columns: dashboard.table.config.columns[domainType],
        loading: dashboard.table.config.loading[domainType],
        isLoaded: dashboard.table.config.isLoaded[domainType],
        userPresets: dashboard.table.config.userPresets[domainType],
      }),
      shallowEqual
    )
  const [popupColumns, setPopupColumns] = useState(
    columns.map(name => Columns[name])
  )
  const generalPreset = getGeneralPresets(domainType)
  const etcPreset = getEtcPresets(domainType)

  const handleConfirm = async ({ preventClose }) => {
    if (shouldSavePreset) {
      try {
        await validate()
      } catch (error) {
        return preventClose()
      }
      const { error, payload } = await dispatch(
        (isPresent(editing) ? updateUserPreset : saveUserPreset)({
          id: editing,
          name: presetName,
          domainType,
          columns: popupColumns,
        })
      )
      if (error) {
        return preventClose()
      }
      dispatch(
        applyUserPreset({
          id: payload.selectedPreset.id,
          domainType,
        })
      )
      success('변경사항이 적용되었습니다.')
    } else {
      dispatch(
        setColumns({
          columns: popupColumns.map(({ name }) => name),
          domainType,
        })
      )
    }
  }

  useEffect(() => {
    if (visible) {
      if (editing === undefined) {
        setShouldSavePreset(false)
        setPresetName('')
      }
      setPopupColumns(columns.map(name => Columns[name]))
    }
  }, [visible, editing, columns])

  useEffect(() => {
    if (editing && !isLoaded && !loading) dispatch(fetchUserPresets(domainType))
  }, [isLoaded, loading, editing, domainType, dispatch])

  useEffect(() => {
    if (!isPresent(editing)) return
    const editingPreset = userPresets.find(
      userPreset => userPreset.id === editing
    )
    if (!isPresent(editingPreset)) return
    setShouldSavePreset(true)
    setPresetName(editingPreset.name)
    setPopupColumns(editingPreset.columns.map(name => Columns[name]))
  }, [editing, userPresets])

  const handleClose = () => {
    dispatch(hidePresetConfigModal(domainType))
  }

  return (
    <Modal on={visible} innerClassName="inner_row_layer">
      <ConfirmModal
        title="열 맞춤 설정"
        handleClose={handleClose}
        onConfirm={handleConfirm}>
        <LoadingWrapper isLoading={loading}>
          <div className="body_row">
            <ScrollablePresetSelector
              columns={popupColumns}
              onChange={setPopupColumns}
              presets={[
                generalPreset,
                MetricPresets.BASIC,
                MetricPresets.ADDITIONAL,
                MetricPresets.PIXEL_AND_SDK,
                MetricPresets.TALK_CHANNEL,
                etcPreset,
              ]}
            />
            <SortableColumnList
              columns={popupColumns}
              onChange={setPopupColumns}
            />
          </div>
          <div
            className={classNames('foot_row', {
              setting_save: shouldSavePreset,
            })}>
            <CheckBox
              label="열 설정 저장"
              disabled={isPresent(editing)}
              checked={shouldSavePreset}
              onCheckedChange={checked => {
                setShouldSavePreset(checked)
                setPresetName('')
              }}
            />
            {shouldSavePreset && (
              <>
                <TextInput
                  value={presetName}
                  onValueChange={setPresetName}
                  maxLength={10}
                  placeholder="열 설정 이름을 입력하세요."
                  error={errors.presetName}
                  trim
                />
                <ErrorText error={errors.presetName} />
              </>
            )}
          </div>
        </LoadingWrapper>
      </ConfirmModal>
    </Modal>
  )
}

export default PresetConfigModal

PresetConfigModal.propTypes = {
  domainType: PropTypes.oneOf([
    DashboardTypes.CAMPAIGN,
    DashboardTypes.AD_GROUP,
    DashboardTypes.KEYWORD,
    DashboardTypes.CREATIVE,
  ]).isRequired,
}
